import React from "react";

import { STAGES } from "./hubspotSnapshot";

// Masquer les gagnés déjà terminés (date de fin de contrat avant juillet 2026).
const WON_END_CUTOFF = "2026-07-01";
const VISIBLE_STAGES = STAGES.map((stage) =>
  stage.key !== "closedwon" ? stage : { ...stage, deals: stage.deals.filter((d) => !d.endDate || d.endDate >= WON_END_CUTOFF) }
);

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "n.c.";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("fr-FR", { year: "numeric", month: "short", day: "numeric" });
};

const isOverdue = (dateString) => {
  if (!dateString) return false;
  return new Date(dateString) < new Date();
};

const stageTotal = (deals) => deals.reduce((sum, d) => sum + (d.amount || 0), 0);

export default function SalesSnapshot() {
  const pipelineTotal = VISIBLE_STAGES.reduce((sum, s) => sum + stageTotal(s.deals), 0);
  const weightedTotal = VISIBLE_STAGES.reduce((sum, s) => sum + stageTotal(s.deals) * s.confidence, 0);
  const pipelineCount = VISIBLE_STAGES.reduce((sum, s) => sum + s.deals.length, 0);

  return (
    <div className="p-4 space-y-4">
      {/* Print styles: hide the nav + the print button when printing to PDF */}
      <style>{`@media print { nav, .no-print { display: none !important; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`}</style>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Sales — Selego</h2>
          <p className="text-sm text-gray-500 mt-1">
            {pipelineCount} deals • Total : {formatCurrency(pipelineTotal)} • Pondéré : {formatCurrency(Math.round(weightedTotal))} • snapshot 20 juil. 2026
          </p>
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="no-print px-3 py-1.5 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-700">
          Imprimer / PDF
        </button>
      </div>

      {/* One section per stage (won → bas de funnel) */}
      {VISIBLE_STAGES.map((stage) => (
        <div key={stage.key} className="bg-white rounded-lg shadow overflow-hidden">
          <div className={`flex items-center justify-between px-4 py-2 border-b ${stage.color}`}>
            <span className="text-sm font-semibold uppercase tracking-wide">
              {stage.label} <span className="font-normal">· {Math.round(stage.confidence * 100)}%</span>
            </span>
            <span className="text-xs font-medium">
              {stage.deals.length} deals • {formatCurrency(stageTotal(stage.deals))} • pondéré {formatCurrency(Math.round(stageTotal(stage.deals) * stage.confidence))}
            </span>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-2 text-left">Deal</th>
                <th className="px-4 py-2 text-right">Montant</th>
                <th className="px-4 py-2 text-right">Confidence</th>
                <th className="px-4 py-2 text-right">Start</th>
                <th className="px-4 py-2 text-right">End</th>
                <th className="px-4 py-2 text-right">Close date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {stage.deals.map((deal, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm text-gray-900">{deal.name}</td>
                  <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right whitespace-nowrap">
                    {formatCurrency(deal.amount)}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-500 text-right whitespace-nowrap">{Math.round(stage.confidence * 100)}%</td>
                  <td className="px-4 py-2 text-sm text-gray-500 text-right whitespace-nowrap">{formatDate(deal.startDate)}</td>
                  <td className="px-4 py-2 text-sm text-gray-500 text-right whitespace-nowrap">{formatDate(deal.endDate)}</td>
                  <td className={`px-4 py-2 text-sm text-right whitespace-nowrap ${isOverdue(deal.closeDate) ? "text-red-600 font-medium" : "text-gray-500"}`}>
                    {formatDate(deal.closeDate)}
                    {isOverdue(deal.closeDate) ? " ⚠️" : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <p className="text-xs text-gray-400">
        ⚠️ = close date dépassée. Confidence = probabilité liée au stage (hs_deal_stage_probability). Pondéré = montant × confidence.
        Start = date de début souhaitée, End = date de fin de contrat prévue. « Fermé gagné » = deals gagnés du pipeline dont le contrat n'est pas terminé avant juillet 2026.
      </p>
    </div>
  );
}
